import { useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { PaymentApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";

interface UsePaymentsGetParams {
  userId: number;
  enabled?: boolean;
}

export const usePaymentsGet = ({
  userId,
  enabled = true,
}: UsePaymentsGetParams) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  return useQuery({
    queryKey: [QueryKeys.Payments, userId],
    queryFn: async () => {
      if (!accessToken) throw new Error("Authentication token is required");
      const service = createAuthenticatedApi(PaymentApi, accessToken);
      try {
        const payments = await service.apiPaymentsUserUserIdGet({
          userId,
          getPaymentByUserIdRequest: { studentId: null },
          xTenantID: tenantId,
        });
        return payments ?? [];
      } catch (error) {
        // No payments yet is a normal state for new members - not a load failure.
        if (
          error instanceof ResponseError &&
          (error.response.status === 404 || error.response.status === 403)
        ) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!accessToken && !!userId && !!tenantId && enabled,
    staleTime: 2 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
